import type { TeamMember } from '../types/teamMember'
import { resolveMemberUuidForStoredPerson } from './teamMemberLookup'

/**
 * Normalize an equipment label so tokens that only differ in punctuation or
 * casing compare equal. The floor-sheet operator fields use the project's
 * dashed catalog names (e.g. `M-500`, `P-600Z`) while the Team Member form now
 * assigns the dash-free labels (e.g. `M500`, `P600z`); both refer to the same
 * physical machine, so we strip non-alphanumerics and uppercase before
 * comparing.
 */
function normalizeEquipmentToken(value: string): string {
  return value.replace(/[^a-z0-9]/gi, '').toUpperCase()
}

/** Base equipment tag for floorsheet operator field keys (CW/CCW → base machine). */
export function baseEquipmentTokenForOperatorFieldKey(fieldKey: string): string | null {
  if (fieldKey === 'IWM') return 'IWM'
  if (fieldKey === 'Manual Welding') return 'Manual Welding'
  if (fieldKey === 'Semi-Automatic Welding') return 'Semi-Automatic Welding'
  if (fieldKey === 'Recorded') return null
  const m = fieldKey.match(/^(.+) (CW|CCW)$/)
  if (m) return m[1]
  return null
}

/** Caller should pass active members only. */
export function filterTeamMembersForOperatorField(
  fieldKey: string,
  projectEquipment: string[],
  members: TeamMember[]
): TeamMember[] {
  if (fieldKey === 'Recorded') {
    const pe = projectEquipment.filter(Boolean).map(normalizeEquipmentToken)
    if (pe.length > 0) {
      return members.filter((m) =>
        (m.assignedEquipment ?? []).some((eq) => pe.includes(normalizeEquipmentToken(eq)))
      )
    }
    return members.filter((m) => m.memberRole === 'operator')
  }
  const token = baseEquipmentTokenForOperatorFieldKey(fieldKey)
  if (!token) return []
  const normalizedToken = normalizeEquipmentToken(token)
  return members.filter((m) =>
    (m.assignedEquipment ?? []).some((eq) => normalizeEquipmentToken(eq) === normalizedToken)
  )
}

/**
 * Back-compat alias for the floor-sheet operator dropdown, which has historic
 * call sites + tests. The canonical helper now lives in
 * `client/src/utils/teamMemberLookup.ts` so the PQR preview signatory pickers
 * can share it.
 */
export const resolveMemberUuidForStoredOperator = resolveMemberUuidForStoredPerson
